/**
 * Clipiti Collect - Collections landing page configuration.
 *
 * ALL user-facing copy and form options live here so the page can be edited
 * from a single file without touching component code. Hebrew, RTL-first.
 */

export const PAGE_VERSION = 'collections-landing-v9';

export const productLabel = 'Clipiti Collect';

export const seo = {
  title: 'גבייה חכמה לעסקים | Clipiti Collect',
  description:
    'מערכת גבייה חכמה לעסקים קטנים: גובים כסף מלקוחות מהר יותר עם קישור או QR במקום, ו-Clipiti מנהלת את המעקב, התזכורות וסגירת התשלום - בלי להחליף את מערכת החשבוניות הקיימת.',
  canonical: 'https://collect.clipiti.com/',
};

export const hero = {
  eyebrow: 'גבו כסף מלקוחות מהר יותר',
  headline: 'אתם מומחים בעסק שלכם - Clipiti מטפלת בגבייה',
  subheadline:
    'פותחים בקשת תשלום ללקוח בתוך רגע - קישור, QR במקום, או שליחה בוואטסאפ - ו-Clipiti מנהלת את המעקב, התזכורות והסגירה כדי שהכסף ייכנס מהר יותר.',
  primaryCta: 'הרשמה לגישה מוקדמת',
  secondaryTrust: 'לא מחליפים מערכת חשבוניות. לא מעבירים כסף דרכנו.',
};

export const delegate = {
  title: 'גבו מהר, בלי לרדוף אחרי תשלומים',
  subtitle:
    'לפעמים צריך לגבות במקום מול הלקוח. לפעמים החשבונית כבר נשלחה והכסף עדיין פתוח. Clipiti עוזרת ללקוח לשלם מהר ומטפלת במעקב עד הסגירה.',
  cards: [
    {
      title: 'קל ללקוח לשלם',
      body: 'בקשת התשלום מגיעה ללקוח בדרך שנוחה לו - QR במקום, וואטסאפ, SMS או מייל - כדי לצמצם חיכוך ולהביא את הכסף מהר יותר.',
    },
    {
      title: 'המעקב רץ בשבילכם',
      body: 'בקשות תשלום, תזכורות בזמן, מעקב אחרי סטטוס וסגירה - קורים בלי שתצטרכו לזכור או להזכיר.',
    },
    {
      title: 'אתם נשארים בשליטה',
      body: 'הכול שקוף: אתם רואים מי שילם ומי עדיין פתוח, מאשרים מה שצריך, ומתערבים רק כשצריך. בלי להעביר כסף דרכנו.',
    },
  ],
};

export const problem = {
  title: 'לא כל גבייה מתחילה בחשבונית - אבל כל גבייה צריכה להיסגר.',
  cards: [
    'לגבות במקום כשהלקוח מוכן לשלם עכשיו',
    'להציג QR מהיר לתשלום בלי לחפש קישור או פרטים',
    'להפוך חשבונית פתוחה לתשלום שקל ללקוח לסגור',
    'לזכור מי עדיין פתוח ומי הבטיח לשלם',
  ],
};

export const workflow = {
  title: 'בקשת תשלום מהירה - QR במקום או שליחה ללקוח',
  steps: [
    'פותחים בקשת תשלום מהירה ללקוח',
    'מציגים QR במקום או שולחים בוואטסאפ, SMS או מייל',
    'Clipiti עוקבת אחרי סטטוס התשלום',
    'תזכורות נשלחות בזמן ובטון הנכון',
    'התשלום נסגר דרך המערכות הקיימות שלכם',
  ],
};

export const value = {
  title: 'לא עוד מערכת חשבוניות',
  bullets: [
    'קל לגבות במקום כשהלקוח כבר איתכם - עם QR מהיר לתשלום',
    'יותר קל ללקוח לשלם - ופחות כסף נשאר פתוח',
    'התשלום לא נשאר פתוח בראש שלכם - Clipiti מטפלת במעקב',
    'המערכות שאתם כבר משתמשים בהן נשארות מקור האמת',
  ],
};

/**
 * Objection-handling block - answers the #1 reason businesses might not need
 * this ("my invoicing tool already sends reminders"). Critical for trustworthy
 * validation: it makes a "no" mean "not interested" rather than "didn't get it".
 */
export const objection = {
  title: 'אבל למערכת החשבוניות שלי כבר יש תזכורות - למה צריך עוד כלי?',
  cards: [
    {
      title: 'תזכורת בודדת מול גבייה שמתבצעת בשבילכם',
      body: 'מערכת החשבוניות שולחת תזכורת אחת ומשאירה אתכם לעקוב. Clipiti מנהלת את ההמשך בשבילכם: מי עדיין פתוח, מתי להזכיר, באיזה טון, ומה קרה אחרי.',
    },
    {
      title: 'תמונה אחת מעל כל הכלים',
      body: 'משלמים בכמה אמצעי סליקה ומוציאים חשבוניות בכמה מקומות? Clipiti מאחדת את סטטוס הגבייה לתמונה אחת, בלי להחליף דבר.',
    },
    {
      title: 'וואטסאפ, לא רק מייל',
      body: 'רוב התזכורות של מערכות החשבוניות יושבות במייל. השיחות האמיתיות קורות בוואטסאפ - ושם Clipiti מטפלת במעקב ובתזכורות.',
    },
  ],
};

/**
 * Optional, honest social-proof / scarcity. `count` is config-driven so it is
 * never fabricated - set to a real waitlist number or leave `show: false`.
 */
export const socialProof = {
  show: false,
  count: 0,
  template: 'כבר נרשמו {count} עסקים לגישה המוקדמת',
  caption: 'מקומות הגישה המוקדמת מוגבלים ומוקצים לפי סדר ההרשמה.',
};

export const pricing = {
  title: 'מחיר ההשקה צפוי להתחיל ב־₪29 לחודש לעסק',
  text: 'נרשמים מוקדם יקבלו עדיפות בגישה והטבת השקה.',
  // Shown inside the form (step 3).
  formNote: 'מחיר ההשקה צפוי להתחיל ב־₪29 לחודש לעסק.',
};

export const finalCta = {
  headline: 'מוכנים לגבות מהר יותר - במקום או אחרי חשבונית?',
  cta: 'בדקו התאמה וקבלו גישה מוקדמת',
};

/** Privacy policy link. Leave href empty to hide the link entirely. */
export const privacy = {
  href: '',
  label: 'מדיניות הפרטיות',
};

// ---------------------------------------------------------------------------
// Lead form configuration
// ---------------------------------------------------------------------------

/** A selectable option. `freeText` marks options that reveal a text input. */
export interface FormOption {
  value: string;
  label: string;
  freeText?: boolean;
}

export const form = {
  startCta: hero.primaryCta,
  steps: {
    labels: ['המערכות שלכם', 'גישה מוקדמת'],
  },

  // Step 1 - current systems
  invoicingPlatform: {
    question: 'באיזו מערכת חשבוניות או הנהלת חשבונות אתם משתמשים היום? (לא חובה)',
    options: [
      { value: 'rivhit', label: 'ריווחית' },
      { value: 'heshbonit_beclick', label: 'חשבונית בקליק' },
      { value: 'morning', label: 'Morning / חשבונית ירוקה' },
      { value: 'icount', label: 'iCount' },
      { value: 'invoice4u', label: 'Invoice4U' },
      { value: 'ezcount', label: 'EZCount' },
      { value: 'hashavshevet', label: 'חשבשבת' },
      { value: 'other', label: 'מערכת אחרת', freeText: true },
      { value: 'none', label: 'לא משתמשים במערכת קבועה' },
    ] as FormOption[],
    otherPlaceholder: 'איזו מערכת? (לא חובה)',
  },

  paymentMethods: {
    question: 'איך הלקוחות משלמים לכם בדרך כלל? (לא חובה)',
    options: [
      { value: 'icredit', label: 'iCredit' },
      { value: 'cardcom', label: 'Cardcom' },
      { value: 'payplus', label: 'PayPlus' },
      { value: 'grow', label: 'Grow / משולם' },
      { value: 'tranzila', label: 'Tranzila' },
      { value: 'bit_paybox', label: 'bit / PayBox' },
      { value: 'bank_transfer', label: 'העברה בנקאית' },
      { value: 'cash_check', label: "מזומן / צ'קים" },
      { value: 'other', label: 'אחר', freeText: true },
    ] as FormOption[],
    otherPlaceholder: 'איך עוד? (לא חובה)',
  },

  // Step 2 - early access (short join step)
  priceCommitment: {
    question: 'מחיר ההשקה: ₪29 לחודש לעסק',
    options: [
      { value: 'join_at_29', label: 'הצטרפות לגישה מוקדמת ב־₪29 לחודש' },
      { value: 'in_at_29', label: 'כן - ב־₪29 לחודש אני בפנים' },
      { value: 'in_if_lower', label: 'אצטרף אם המחיר יהיה נמוך יותר' },
      { value: 'if_proven_savings', label: 'רק אם יוכיח לי חיסכון בזמן ובכסף' },
      { value: 'not_now', label: 'לא מתאים לי כרגע' },
    ] as FormOption[],
  },

  contact: {
    firstName: { label: 'שם פרטי', placeholder: 'איך לפנות אליך?' },
    businessName: { label: 'שם העסק', placeholder: 'שם העסק' },
    email: { label: 'אימייל', placeholder: 'name@example.com' },
    mobile: { label: 'נייד / וואטסאפ', placeholder: '050-0000000' },
    businessType: { label: 'תחום עיסוק', placeholder: 'לדוגמה: מאמן, יועצת, בעל מוסך' },
    contactHelper: 'אימייל ונייד הם שדות חובה כדי שנוכל לפתוח לכם גישה מוקדמת.',
    consent: 'שליחה מאשרת קבלת עדכונים לגבי הגישה המוקדמת למוצר.',
    submit: 'הצטרפות לגישה מוקדמת',
    submitting: 'שולח…',
  },
};

export const success = {
  title: 'תודה - קיבלנו את הפרטים שלך',
  body:
    'נעדכן אותך כשהגישה המוקדמת תיפתח, ונשתמש בתשובות שלך כדי לתעדף את המערכות והיכולות החשובות ביותר.',
  // {platform} is replaced with the selected invoicing system label when available.
  platformLine: 'סימנת שאתם משתמשים ב־{platform} - נשמור על זה בראש סדר העדיפויות.',
};

export const errors = {
  required: 'שדה חובה',
  contactRequired: 'יש למלא אימייל ונייד',
  emailInvalid: 'כתובת אימייל לא תקינה',
  mobileInvalid: 'מספר נייד לא תקין',
  consentRequired: 'צריך לאשר קבלת עדכונים כדי להמשיך',
  selectOne: 'יש לבחור לפחות תשובה אחת',
  otherRequired: 'נא לפרט',
  tooLong: 'הטקסט ארוך מדי',
  network: 'משהו השתבש בשליחה. נסו שוב בעוד רגע.',
  generic: 'אירעה שגיאה. נסו שוב.',
};
